import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    CircularProgress,
} from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { intl, DmsButton, fetchData, HTTP_STATUS } from 'dmsbasereact';
import { DocumentUploadSlot } from '../DocumentUploadSlot/DocumentUploadSlot';

const ACCEPTED_FILES = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'application/pdf': ['.pdf'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
};

export const AttendanceUploadModal = ({ open, onClose, onConfirm }) => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFilePk, setUploadedFilePk] = useState(null);

    const handleFileSelect = (docName, selectedFile) => {
        setFile(selectedFile);
        setUploadedFilePk(null);
    };

    const handleConfirm = async () => {
        if (file && !uploadedFilePk) {
            setIsUploading(true);
            try {
                const formData = new FormData();
                formData.append('file', file);

                const response = await fetchData(
                    '/training_material/attendance-file-upload',
                    'POST',
                    true,
                    formData,
                    false,
                    null,
                    null,
                    null,
                    'multipart/form-data',
                    false
                );

                if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                    const filePk = response.data?.data?.pk;
                    setUploadedFilePk(filePk);
                    onConfirm(filePk);
                    handleClose();
                } else {
                    console.error('Upload failed:', response);
                }
            } catch (err) {
                console.error('Failed to upload attendance file:', err);
            } finally {
                setIsUploading(false);
            }
        } else {
            onConfirm(uploadedFilePk);
            handleClose();
        }
    };

    const handleClose = () => {
        setFile(null);
        setUploadedFilePk(null);
        onClose();
    };

    const handleCancel = () => {
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListAltIcon />
                {intl.get("AttendanceListUpload").d("Attendance List Upload")}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {intl.get("AttendanceUploadDescription").d(
                            "As the institution's representative, you can replicate this training for other healthcare professionals and upload an attendance list here."
                        )}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {intl.get("AttendanceAcceptedFormats").d("Accepted formats: JPG, JPEG, PNG, PDF, PPT, PPTX")}
                    </Typography>

                    <Box sx={{ minHeight: 120 }}>
                        <DocumentUploadSlot
                            docName={intl.get("AttendanceList").d("Attendance List")}
                            file={file}
                            onFileSelect={handleFileSelect}
                            disabled={isUploading}
                            accept={ACCEPTED_FILES}
                        />
                    </Box>

                    {isUploading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress size={24} sx={{ mr: 1 }} />
                            <Typography variant="body2">
                                {intl.get("Uploading").d("Uploading...")}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <DmsButton
                    onClick={handleCancel}
                    text={intl.get("Cancel").d("Cancel")}
                    disabled={isUploading}
                />
                <DmsButton
                    onClick={handleConfirm}
                    variant="primary"
                    text={intl.get("OK").d("OK")}
                    disabled={isUploading}
                />
            </DialogActions>
        </Dialog>
    );
};

AttendanceUploadModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
};
